import { BotMessageSquare, ChevronDown, CircleCheck } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuPortal,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { ChatAgentSelectionState } from '@/hooks'
import { CHAT_AGENT_PROVIDER_LABELS } from '@/types'

interface ChatAgentSelectorProps {
  agent: ChatAgentSelectionState
  disabled: boolean
  showEffort: boolean
}

interface AgentOptionProps {
  value: string
  label: string
  checked: boolean
  testId: string
}

const menuClassName = 'rounded-lg p-1.5 shadow-elevated'
const rowClassName =
  'min-w-[230px] gap-2 rounded-md px-3 py-2.5 font-semibold text-foreground data-[state=open]:bg-muted focus:bg-muted [&>svg:last-child]:ml-0 [&>svg:last-child]:size-5'
const rowLabelClassName = 'mr-8'
const rowValueClassName = 'ml-auto truncate font-normal text-foreground'
const optionClassName =
  'min-w-[180px] justify-between gap-8 rounded-md py-2 pl-3 pr-2 text-foreground focus:bg-muted data-[state=checked]:bg-muted [&>span:first-child]:hidden'

const providerLabel = (provider: string) => CHAT_AGENT_PROVIDER_LABELS[provider] ?? provider

function AgentOption({ value, label, checked, testId }: Readonly<AgentOptionProps>) {
  return (
    <DropdownMenuRadioItem value={value} className={optionClassName} data-testid={testId}>
      <span>{label}</span>
      {checked && <CircleCheck className="h-5 w-5 shrink-0 text-success-foreground" aria-hidden="true" />}
    </DropdownMenuRadioItem>
  )
}

export function ChatAgentSelector({ agent, disabled, showEffort }: Readonly<ChatAgentSelectorProps>) {
  const { providers, providerOption, selection, models, effortsFor, modelLabel, effortLabel, isReady, isError } = agent
  const modelSelectable = !!providerOption?.supportsModelSelection
  const modelRowEnabled = modelSelectable && models.length > 0
  const efforts = selection?.model ? effortsFor(selection.model) : []
  const effortSelectable = !!providerOption?.supportsEffortSelection && efforts.length > 1

  const triggerText = !isReady
    ? 'Loading AI providers…'
    : isError
    ? 'AI providers unavailable'
    : !selection
    ? 'No AI provider'
    : selection.model
      ? `${providerLabel(selection.agentProvider)} ${modelLabel(selection.model)}`
      : providerLabel(selection.agentProvider)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild disabled={disabled || !isReady || isError}>
        <button
          type="button"
          aria-label={`AI provider, model and effort: ${triggerText}`}
          data-testid="agent-selector-trigger"
          className="inline-flex h-7 max-w-[240px] shrink-0 items-center gap-1.5 rounded-full border border-primary/30 bg-primary/10 px-2.5 text-xs font-medium text-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50"
        >
          <BotMessageSquare className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
          <span className="truncate">{triggerText}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-60" aria-hidden="true" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" side="top" className={menuClassName} data-testid="agent-selector-menu">
        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={rowClassName} disabled={providers.length === 0} data-testid="agent-selector-provider">
            <span className={rowLabelClassName}>Provider</span>
            <span className={rowValueClassName}>{selection ? providerLabel(selection.agentProvider) : ''}</span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent aria-label="Provider" className={menuClassName}>
              <DropdownMenuRadioGroup value={selection?.agentProvider ?? ''} onValueChange={agent.selectProvider}>
                {providers.map((p) => (
                  <AgentOption
                    key={p.provider}
                    value={p.provider}
                    label={providerLabel(p.provider)}
                    checked={selection?.agentProvider === p.provider}
                    testId={`agent-provider-option-${p.provider}`}
                  />
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        <DropdownMenuSeparator className="mx-2" />

        <DropdownMenuSub>
          <DropdownMenuSubTrigger className={rowClassName} disabled={!modelRowEnabled} data-testid="agent-selector-model">
            <span className={rowLabelClassName}>Model</span>
            <span className={rowValueClassName}>
              {selection && !modelSelectable ? 'Set by the platform' : selection?.model ? modelLabel(selection.model) : ''}
            </span>
          </DropdownMenuSubTrigger>
          <DropdownMenuPortal>
            <DropdownMenuSubContent aria-label="Model" className={`${menuClassName} max-h-80 overflow-y-auto`}>
              <DropdownMenuRadioGroup value={selection?.model ?? ''} onValueChange={agent.selectModel}>
                {models.map((m) => (
                  <AgentOption
                    key={m.id}
                    value={m.id}
                    label={m.label}
                    checked={selection?.model === m.id}
                    testId={`agent-model-option-${m.id}`}
                  />
                ))}
              </DropdownMenuRadioGroup>
            </DropdownMenuSubContent>
          </DropdownMenuPortal>
        </DropdownMenuSub>

        {showEffort && (
          <>
            <DropdownMenuSeparator className="mx-2" />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger className={rowClassName} disabled={!effortSelectable} data-testid="agent-selector-effort">
                <span className={rowLabelClassName}>Effort</span>
                <span className={rowValueClassName}>
                  {selection && !modelSelectable ? 'Set by the platform' : selection?.effort ? effortLabel(selection.effort) : ''}
                </span>
              </DropdownMenuSubTrigger>
              <DropdownMenuPortal>
                <DropdownMenuSubContent aria-label="Effort" className={menuClassName}>
                  <DropdownMenuRadioGroup value={selection?.effort ?? ''} onValueChange={agent.selectEffort}>
                    {efforts.map((e) => (
                      <AgentOption
                        key={e}
                        value={e}
                        label={effortLabel(e)}
                        checked={selection?.effort === e}
                        testId={`agent-effort-option-${e}`}
                      />
                    ))}
                  </DropdownMenuRadioGroup>
                </DropdownMenuSubContent>
              </DropdownMenuPortal>
            </DropdownMenuSub>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
